import {
  Catalog_VariantPackGroupItemsExpandedFieldsFragment,
  Catalog_VariantPackGroupItemsExpandedQuery,
  Catalog_VariantPackGroupItemsExpandedQueryVariables,
  Catalog_VariantPackGroupItemsQuery,
  Catalog_VariantPackGroupItemsQueryVariables,
  SearchCatalog_VariantPackFieldsFragment,
} from '@/types';
import {
  compareByCategoryName,
  GroupItemsCategory,
  useCatalogVariantPackGroups,
} from './use-catalog-variant-pack-groups';
import { gql } from '@apollo/client';
import {
  groupItemListOptions,
  useAdaptExpandedGroupItemToItemListOption,
  useAdaptVariantPackToItemListOption,
} from '@/contexts/item-context-utils';
import {
  ItemDisplayOptionsWithSort,
  useItemDisplayOptions,
  useNewOrderContext,
} from '@/contexts/new-order-context';
import { ItemListOption } from '@/contexts/item-context-types';
import { NULL_CATEGORY_VALUE } from '@/utils/category';
import { useCatalogLazyCarouselsEnabled } from './dynamic-config/use-catalog-lazy-carousels-enabled';
import { useDebouncedCallback } from 'use-debounce';
import {
  useDynamicConfigValue,
  useSupplierDynamicConfigValue,
} from './use-dynamic-config-value';
import { useEffect, useMemo, useState } from 'react';
import { useItemQueryVariables } from './use-item-query-variables';
import { useLimitedPerformanceEnabled } from './use-limited-performance';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from './use-wrapped-query';
import {
  VARIANTS_PACK_FRAGMENT,
  VARIANTS_PACK_GROUP_ITEMS_FRAGMENT,
} from '@/contexts/items-context-queries';

const UPDATE_ITEMS_LIST_DEBOUNCE_DELAY_MS = 1000;

export const VARIANT_PACK_GROUP_ITEMS_QUERY = gql`
  query Catalog_VariantPackGroupItems(
    $itemLimit: Int!
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
  ) {
    getSupplierVariantPackGroupItems(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      variant_pack_group_item_limit: $itemLimit
    ) {
      variant_pack {
        ...ItemsContext_VariantsPackFields
      }
      variant_pack_group {
        ...ItemsContext_VariantsPackGroupItemsFields
      }
      variant_pack_group_display_name
      variant_pack_group_item_count
    }
  }

  ${VARIANTS_PACK_GROUP_ITEMS_FRAGMENT}
  ${VARIANTS_PACK_FRAGMENT}
`;

// The consolidated ("expanded") variant of Catalog_VariantPackGroupItems: the
// backend action returns the variant pack, group and salt data pre-assembled.
export const VARIANT_PACK_GROUP_ITEMS_EXPANDED_FIELDS_FRAGMENT = gql`
  fragment Catalog_ExpandedSaltInfo on ExpandedSaltItemInfo {
    name
    description
    image_urls
  }

  fragment Catalog_VariantPackGroupItemsExpandedFields on GetSupplierVariantPackGroupItemExpandedResponse {
    variant_pack_group_id
    variant_pack_group_display_name
    variant_pack_group_item_count
    variant_pack_id
    variant_pack_group_data {
      uuid
      display_image_url
    }
    variant_pack_data {
      uuid
      external_item_id
      lead_time_cutoff_time
      lead_time_days
      metadata
      prebook_config
      upc_list
      item {
        uuid
        category
        description
        display_name
        extra_keywords
        photo_url_list
        restaurant_item_overrides {
          uuid
          display_name
          category
        }
      }
      pack {
        uuid
        base_unit
        max_unit_size
        min_unit_size
        unit
        unit_count
      }
      modifier_options {
        modifier_option {
          uuid
          name
          sort_order
          modifier_option_group {
            uuid
            name
            sort_order
          }
        }
      }
      variant_pack_group_memberships {
        uuid
        variant_pack_group_uuid
      }
      gtin_metadata {
        uuid
        gtin
        metadata
      }
    }
    salt_data {
      ...Catalog_ExpandedSaltInfo
    }
  }
`;

// Without $variantPackGroupUUIDs this returns every group. The lazy loader
// passes specific groups, with "Other" requested via $includeNullGroupItems
export const VARIANT_PACK_GROUP_ITEMS_EXPANDED_QUERY = gql`
  query Catalog_VariantPackGroupItemsExpanded(
    $itemLimit: Int!
    $locale: String!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $includeSalt: Boolean!
    $variantPackGroupUUIDs: [uuid!]
    $includeNullGroupItems: Boolean
  ) {
    getSupplierVariantPackGroupItemsExpanded(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      variant_pack_group_item_limit: $itemLimit
      include_salt: $includeSalt
      include_group_memberships: true
      locale: $locale
      variant_pack_group_ids: $variantPackGroupUUIDs
      include_null_variant_pack_group_items: $includeNullGroupItems
    ) {
      ...Catalog_VariantPackGroupItemsExpandedFields
    }
  }

  ${VARIANT_PACK_GROUP_ITEMS_EXPANDED_FIELDS_FRAGMENT}
`;

export const useVariantPackGroupItems = () => {
  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();
  const adaptExpandedGroupItemToItemListOption =
    useAdaptExpandedGroupItemToItemListOption();
  const itemDisplayOptions = useItemDisplayOptions();
  const itemQueryVariables = useItemQueryVariables();
  const limitedPerformanceEnabled = useLimitedPerformanceEnabled();
  const { isGuestMode } = useUserContext();
  const { restaurantUUID, supplierUUID } = useNewOrderContext();

  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');
  const expandedEnabled = useDynamicConfigValue(
    'catalog_group_items_expanded_enabled',
  );
  const lazyEnabled = useCatalogLazyCarouselsEnabled();
  const specialCharsLast = useSupplierDynamicConfigValue(
    'sort_categories_with_special_chars_last',
    false,
  );

  const [variantPackGroupItemsList, setVariantPackGroupItemsList] = useState<
    GroupItemsCategory[] | null
  >(null);

  const lazyGroups = useCatalogVariantPackGroups({ skip: !lazyEnabled });

  const { data, hasResponse } = useWrappedQuery<
    Catalog_VariantPackGroupItemsQuery,
    Catalog_VariantPackGroupItemsQueryVariables
  >(VARIANT_PACK_GROUP_ITEMS_QUERY, {
    context: {
      anonymous: isGuestMode,
      noBatch: true,
    },
    fetchPolicy: 'no-cache',
    skip:
      !restaurantUUID ||
      !supplierUUID ||
      !itemGroupsEnabled ||
      expandedEnabled ||
      lazyEnabled,
    variables: {
      ...itemQueryVariables,
      itemLimit: limitedPerformanceEnabled ? 5 : 20,
      restaurantUUID: restaurantUUID!,
      supplierUUID: supplierUUID!,
    },
  });

  const { data: expandedData, hasResponse: expandedHasResponse } =
    useWrappedQuery<
      Catalog_VariantPackGroupItemsExpandedQuery,
      Catalog_VariantPackGroupItemsExpandedQueryVariables
    >(VARIANT_PACK_GROUP_ITEMS_EXPANDED_QUERY, {
      context: {
        anonymous: isGuestMode,
        noBatch: true,
      },
      fetchPolicy: 'no-cache',
      skip:
        !restaurantUUID ||
        !supplierUUID ||
        !itemGroupsEnabled ||
        !expandedEnabled ||
        lazyEnabled,
      variables: {
        includeSalt: !itemQueryVariables.skipSaltApi,
        itemLimit: limitedPerformanceEnabled ? 5 : 20,
        locale: itemQueryVariables.locale,
        restaurantUUID: restaurantUUID!,
        supplierUUID: supplierUUID!,
      },
    });

  const debouncedUpdateItemsList = useDebouncedCallback(
    (
      adaptVariantPackToItemListOption: (
        vp: SearchCatalog_VariantPackFieldsFragment,
      ) => ItemListOption,
      items: Catalog_VariantPackGroupItemsQuery['getSupplierVariantPackGroupItems'],
      itemDisplayOptions: ItemDisplayOptionsWithSort,
    ) => {
      const variantPackGroupItemsMap: {
        [category: string]: ItemListOption[];
      } = {};

      const list = items
        .reduce<GroupItemsCategory[]>((result, groupItem) => {
          const variantPackGroup = groupItem.variant_pack_group;
          const variantPack = groupItem.variant_pack;
          const variantPackGroupId =
            groupItem.variant_pack_group?.uuid || NULL_CATEGORY_VALUE;

          if (!variantPack) {
            return result;
          }

          if (!variantPackGroupItemsMap[variantPackGroupId]) {
            variantPackGroupItemsMap[variantPackGroupId] = [];
            result.push({
              category: {
                imageUrl: variantPackGroup?.display_image_url,
                name: groupItem.variant_pack_group_display_name || null,
                uuid: variantPackGroupId,
              },
              categoryItemCount: groupItem.variant_pack_group_item_count,
              items: variantPackGroupItemsMap[variantPackGroupId],
            });
          }
          variantPackGroupItemsMap[variantPackGroupId].push(
            adaptVariantPackToItemListOption(variantPack),
          );
          return result;
        }, [])
        .sort((a, b) =>
          compareByCategoryName(a.category, b.category, specialCharsLast),
        )
        .map((group) => ({
          ...group,
          items: groupItemListOptions(group.items, itemDisplayOptions),
        }));

      setVariantPackGroupItemsList(list);
    },
    UPDATE_ITEMS_LIST_DEBOUNCE_DELAY_MS,
  );

  const debouncedUpdateExpandedItemsList = useDebouncedCallback(
    (
      adaptExpandedGroupItem: ReturnType<
        typeof useAdaptExpandedGroupItemToItemListOption
      >,
      entries: Catalog_VariantPackGroupItemsExpandedFieldsFragment[],
      itemDisplayOptions: ItemDisplayOptionsWithSort,
    ) => {
      const variantPackGroupItemsMap: {
        [category: string]: ItemListOption[];
      } = {};

      const list = entries
        .reduce<GroupItemsCategory[]>((result, entry) => {
          const variantPack = entry.variant_pack_data;
          const variantPackGroupId =
            entry.variant_pack_group_data?.uuid || NULL_CATEGORY_VALUE;

          if (!variantPack) {
            return result;
          }

          if (!variantPackGroupItemsMap[variantPackGroupId]) {
            variantPackGroupItemsMap[variantPackGroupId] = [];
            result.push({
              category: {
                imageUrl: entry.variant_pack_group_data?.display_image_url,
                name: entry.variant_pack_group_display_name || null,
                uuid: variantPackGroupId,
              },
              categoryItemCount: entry.variant_pack_group_item_count,
              items: variantPackGroupItemsMap[variantPackGroupId],
            });
          }
          variantPackGroupItemsMap[variantPackGroupId].push(
            adaptExpandedGroupItem(variantPack, entry.salt_data),
          );
          return result;
        }, [])
        .sort((a, b) =>
          compareByCategoryName(a.category, b.category, specialCharsLast),
        )
        .map((group) => ({
          ...group,
          items: groupItemListOptions(group.items, itemDisplayOptions),
        }));

      setVariantPackGroupItemsList(list);
    },
    UPDATE_ITEMS_LIST_DEBOUNCE_DELAY_MS,
  );

  useEffect(() => {
    if (lazyEnabled) {
      return;
    }

    if (expandedEnabled) {
      if (expandedHasResponse) {
        debouncedUpdateExpandedItemsList(
          adaptExpandedGroupItemToItemListOption,
          expandedData?.getSupplierVariantPackGroupItemsExpanded ?? [],
          itemDisplayOptions,
        );
      }
      return;
    }

    if (hasResponse) {
      debouncedUpdateItemsList(
        adaptVariantPackToItemListOption,
        data?.getSupplierVariantPackGroupItems ?? [],
        itemDisplayOptions,
      );
    }
  }, [
    adaptExpandedGroupItemToItemListOption,
    adaptVariantPackToItemListOption,
    data,
    debouncedUpdateExpandedItemsList,
    debouncedUpdateItemsList,
    expandedData,
    expandedEnabled,
    expandedHasResponse,
    hasResponse,
    itemDisplayOptions,
    lazyEnabled,
    specialCharsLast,
  ]);

  const list = lazyEnabled ? lazyGroups : variantPackGroupItemsList;

  return useMemo(
    () => ({
      // Renders the list after the first time the response is adapted and
      // there's a list to render (which may be empty)
      hasResponse: Boolean(list),
      variantPackGroupItemsList: list ?? [],
    }),
    [list],
  );
};
